(() => {
  if (window.__matriarchCustomQuickViewLoaded) return;
  window.__matriarchCustomQuickViewLoaded = true;

  const chooseSelector = '.quick-add__button--choose, quick-add-component[data-quick-add-button="choose"] .quick-add__button';
  const dialogId = 'MatriarchQuickView';
  const cache = new Map();
  let activeProductUrl = '';
  let activeProduct = null;
  let selectedOptions = [];
  let activeVariant = null;
  let lastTrigger = null;

  const getDialog = () => document.getElementById(dialogId);
  const getContent = () => getDialog()?.querySelector('[data-mqv-content]');
  const getStatus = () => getDialog()?.querySelector('[data-mqv-status]');

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const productUrlFromTrigger = (trigger) => {
    const quickAdd = trigger.closest('quick-add-component');
    try {
      if (quickAdd && typeof quickAdd.productPageUrl === 'string' && quickAdd.productPageUrl) {
        return quickAdd.productPageUrl;
      }
    } catch (_error) {}

    const productCard = trigger.closest('product-card');
    const cardLink = productCard?.querySelector('a[ref="productCardLink"], a.product-card__link[href]');
    if (cardLink instanceof HTMLAnchorElement && cardLink.href) return cardLink.href;

    const hotspot = trigger.closest('product-hotspot-component');
    const hotspotLink = hotspot?.querySelector('a[ref="productLink"][href]');
    if (hotspotLink instanceof HTMLAnchorElement && hotspotLink.href) return hotspotLink.href;

    return '';
  };

  const productJsonUrl = (productUrl) => {
    const url = new URL(productUrl, window.location.origin);
    url.hash = '';
    url.search = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.toString();
  };

  const variantOptions = (variant) => {
    if (Array.isArray(variant?.options)) return variant.options.map(String);
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null).map(String);
  };

  const normalizeOptions = (product) => {
    const rawOptions = Array.isArray(product.options) ? product.options : [];
    return rawOptions.map((option, index) => {
      const name = typeof option === 'string' ? option : option?.name || `Option ${index + 1}`;
      let values = typeof option === 'object' && Array.isArray(option?.values) ? option.values.map(String) : [];
      if (!values.length) {
        values = [...new Set(product.variants.map((variant) => variantOptions(variant)[index]).filter(Boolean))];
      }
      return { name, position: index + 1, values };
    });
  };

  const imageSource = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const resizeImage = (source, width) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      return source;
    }
  };

  const allProductImages = (product) => {
    const sources = [];
    const add = (source) => {
      const normalized = imageSource(source);
      if (normalized && !sources.includes(normalized)) sources.push(normalized);
    };

    add(product.featured_image);
    (product.images || []).forEach(add);
    (product.variants || []).forEach((variant) => add(variant.featured_image));
    return sources;
  };

  const currencyCode = () =>
    window.Shopify?.currency?.active || window.Shopify?.currency?.current || 'CAD';

  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: currencyCode(),
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const findVariant = (options) =>
    activeProduct?.variants?.find((variant) => {
      const values = variantOptions(variant);
      return options.every((value, index) => values[index] === value);
    }) || null;

  const representativeVariant = (optionIndex, value) => {
    const matches = activeProduct.variants.filter((variant) => variantOptions(variant)[optionIndex] === value);
    return matches.find((variant) => variant.available) || matches[0] || null;
  };

  const valueHasAvailableVariant = (optionIndex, value) =>
    activeProduct.variants.some(
      (variant) => variant.available && variantOptions(variant)[optionIndex] === value
    );

  const isColorOption = (name) => /colou?r/i.test(name);

  const fallbackColor = (value) => {
    const normalized = String(value).trim().toLowerCase();
    const map = {
      black: '#111111', white: '#ffffff', grey: '#a5a5a5', gray: '#a5a5a5',
      teal: '#2f9da1', peach: '#df8f6b', navy: '#1d2b43', blue: '#426f9f',
      red: '#9a3434', green: '#56705d', olive: '#707250', brown: '#765849',
      beige: '#d8c7a7', cream: '#eee6d7', pink: '#d99caf', purple: '#745b86',
      orange: '#d78143', yellow: '#ddb94c',
    };
    return map[normalized] || normalized.replace(/\s+/g, '-');
  };

  const swatchStyle = (optionIndex, value) => {
    const variant = representativeVariant(optionIndex, value);
    const image = imageSource(variant?.featured_image);
    if (image) return `background-image:url('${escapeHtml(resizeImage(image, 160))}')`;
    return `background-color:${escapeHtml(fallbackColor(value))}`;
  };

  const optionMarkup = (option, optionIndex) => {
    const selected = selectedOptions[optionIndex] || '';
    const color = isColorOption(option.name);
    const values = option.values.map((value) => {
      const soldOut = !valueHasAvailableVariant(optionIndex, value);
      const selectedClass = value === selected ? ' is-selected' : '';
      const soldOutClass = soldOut ? ' is-sold-out' : '';
      const colorClass = color ? ' matriarch-quick-view__value--color' : '';
      const content = color
        ? `<span class="matriarch-quick-view__swatch" style="${swatchStyle(optionIndex, value)}"></span><span class="visually-hidden">${escapeHtml(value)}</span>`
        : escapeHtml(value);

      return `<button type="button" class="matriarch-quick-view__value${colorClass}${selectedClass}${soldOutClass}" data-mqv-option-index="${optionIndex}" data-mqv-option-value="${escapeHtml(value)}" aria-pressed="${value === selected}" ${soldOut ? 'disabled' : ''} title="${escapeHtml(value)}${soldOut ? ' - Sold out' : ''}">${content}</button>`;
    }).join('');

    return `<fieldset class="matriarch-quick-view__option"><div class="matriarch-quick-view__option-head"><legend class="matriarch-quick-view__option-name">${escapeHtml(option.name)}</legend><span class="matriarch-quick-view__selected-value">${escapeHtml(selected)}</span></div><div class="matriarch-quick-view__values">${values}</div></fieldset>`;
  };

  const imageMarkup = (images, currentImage) => {
    const initial = currentImage || images[0] || '';
    const thumbs = images.slice(0, 8).map((source, index) => {
      const active = source === initial || (!currentImage && index === 0);
      return `<button type="button" class="matriarch-quick-view__thumb${active ? ' is-active' : ''}" data-mqv-image="${escapeHtml(source)}" aria-label="View product image ${index + 1}"><img src="${escapeHtml(resizeImage(source, 180))}" alt="" loading="lazy"></button>`;
    }).join('');

    return `<div class="matriarch-quick-view__media"><div class="matriarch-quick-view__image-stage"><img class="matriarch-quick-view__image" data-mqv-main-image src="${escapeHtml(resizeImage(initial, 1200))}" alt="${escapeHtml(activeProduct.title)}"></div>${thumbs ? `<div class="matriarch-quick-view__thumbs" aria-label="Product images">${thumbs}</div>` : ''}</div>`;
  };

  const render = () => {
    const content = getContent();
    if (!content || !activeProduct) return;

    const options = normalizeOptions(activeProduct);
    activeVariant = findVariant(selectedOptions) || activeProduct.variants.find((variant) => variant.available) || activeProduct.variants[0];
    if (activeVariant && !findVariant(selectedOptions)) selectedOptions = variantOptions(activeVariant);

    const images = allProductImages(activeProduct);
    const variantImage = imageSource(activeVariant?.featured_image);
    const compare = Number(activeVariant?.compare_at_price) > Number(activeVariant?.price)
      ? `<span class="matriarch-quick-view__compare-price">${formatMoney(activeVariant.compare_at_price)}</span>`
      : '';
    const available = Boolean(activeVariant?.available);
    const addText = available ? 'Add to Cart' : 'Sold Out';

    content.innerHTML = `<div class="matriarch-quick-view__grid">${imageMarkup(images, variantImage)}<div class="matriarch-quick-view__details"><p class="matriarch-quick-view__eyebrow">Quick View</p><h2 class="matriarch-quick-view__title" id="MatriarchQuickViewTitle">${escapeHtml(activeProduct.title)}</h2><div class="matriarch-quick-view__price-row"><span class="matriarch-quick-view__price" data-mqv-price>${formatMoney(activeVariant?.price)}</span>${compare}</div>${options.length ? `<div class="matriarch-quick-view__options">${options.map(optionMarkup).join('')}</div>` : ''}<div class="matriarch-quick-view__actions"><button type="button" class="matriarch-quick-view__add" data-mqv-add ${available ? '' : 'disabled'}>${addText}</button><p class="matriarch-quick-view__message" data-mqv-message role="status" aria-live="polite"></p><a class="matriarch-quick-view__details-link" href="${escapeHtml(activeProductUrl)}">View full product details</a></div></div></div>`;

    const status = getStatus();
    if (status) status.textContent = `${activeProduct.title} quick view loaded`;
  };

  const showLoading = () => {
    const content = getContent();
    if (content) {
      content.innerHTML = '<div class="matriarch-quick-view__loading" role="status" aria-live="polite"><span class="matriarch-quick-view__spinner" aria-hidden="true"></span><span>Loading product</span></div>';
    }
  };

  const showError = (productUrl) => {
    const content = getContent();
    if (!content) return;
    content.innerHTML = `<div class="matriarch-quick-view__error" role="alert"><strong>Unable to load quick view</strong><a href="${escapeHtml(productUrl)}">Open the product page</a></div>`;
  };

  const openDialog = () => {
    const dialog = getDialog();
    if (!(dialog instanceof HTMLDialogElement)) return false;
    if (!dialog.open) dialog.showModal();
    return true;
  };

  const closeDialog = () => {
    const dialog = getDialog();
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  };

  const loadProduct = async (productUrl) => {
    activeProductUrl = productUrl;
    const requestedVariantId = new URL(productUrl, window.location.origin).searchParams.get('variant');
    const jsonUrl = productJsonUrl(productUrl);

    try {
      let product = cache.get(jsonUrl);
      if (!product) {
        const response = await fetch(jsonUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        product = await response.json();
        cache.set(jsonUrl, product);
      }

      activeProduct = product;
      const requestedVariant = requestedVariantId
        ? product.variants.find((variant) => String(variant.id) === String(requestedVariantId))
        : null;
      const initialVariant = requestedVariant || product.variants.find((variant) => variant.available) || product.variants[0];
      selectedOptions = variantOptions(initialVariant);
      activeVariant = initialVariant;
      render();
    } catch (error) {
      console.error('[custom-quick-view]', error);
      showError(productUrl);
    }
  };

  const chooseFallbackVariant = (optionIndex, value) => {
    const nextOptions = [...selectedOptions];
    nextOptions[optionIndex] = value;
    const exact = findVariant(nextOptions);
    if (exact?.available) return exact;

    const candidates = activeProduct.variants.filter(
      (variant) => variant.available && variantOptions(variant)[optionIndex] === value
    );
    if (!candidates.length) return exact;

    candidates.sort((a, b) => {
      const aOptions = variantOptions(a);
      const bOptions = variantOptions(b);
      const aScore = aOptions.reduce((score, candidate, index) => score + (index !== optionIndex && candidate === selectedOptions[index] ? 1 : 0), 0);
      const bScore = bOptions.reduce((score, candidate, index) => score + (index !== optionIndex && candidate === selectedOptions[index] ? 1 : 0), 0);
      return bScore - aScore;
    });
    return candidates[0];
  };

  const updateMainImage = (source) => {
    const dialog = getDialog();
    const mainImage = dialog?.querySelector('[data-mqv-main-image]');
    if (mainImage instanceof HTMLImageElement && source) {
      mainImage.src = resizeImage(source, 1200);
    }
    dialog?.querySelectorAll('[data-mqv-image]').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.getAttribute('data-mqv-image') === source);
    });
  };

  const replaceCartDrawerFromHtml = (html) => {
    if (!html) return;
    const current = document.getElementById('shopify-section-cart-drawer-section');
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    const next = wrapper.querySelector('#shopify-section-cart-drawer-section');
    if (current && next) current.replaceWith(next);
    else if (current) current.innerHTML = html;
  };

  const refreshCartUi = async () => {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const [sectionsResponse, cartResponse] = await Promise.all([
        fetch(`${root}?sections=cart-drawer-section`, { credentials: 'same-origin' }),
        fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
      ]);

      if (sectionsResponse.ok) {
        const sections = await sectionsResponse.json();
        replaceCartDrawerFromHtml(sections?.['cart-drawer-section']);
      }

      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const count = Number(cart.item_count) || 0;
        document.querySelectorAll('.cart-bubble').forEach((bubble) => bubble.classList.toggle('visually-hidden', count === 0));
        document.querySelectorAll('.cart-bubble__text-count').forEach((element) => {
          element.textContent = count > 99 ? '' : String(count);
          element.classList.toggle('hidden', count === 0);
        });
      }
    } catch (error) {
      console.warn('[custom-quick-view] Cart UI refresh failed', error);
    }
  };

  const openCartDrawer = async () => {
    try {
      await customElements.whenDefined('theme-drawer');
    } catch (_error) {}

    requestAnimationFrame(() => {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      document.querySelector('[data-testid="cart-drawer-trigger"], [aria-controls="cart-drawer"]')?.click();
    });
  };

  const addToCart = async (button) => {
    const message = getDialog()?.querySelector('[data-mqv-message]');
    if (!activeVariant?.available || !(button instanceof HTMLButtonElement)) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    if (message) message.textContent = '';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: activeVariant.id, quantity: 1 }] }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item to cart.');

      button.textContent = 'Added';
      await refreshCartUi();
      closeDialog();
      await openCartDrawer();
    } catch (error) {
      if (message) message.textContent = error.message || 'Unable to add this item to cart.';
      button.disabled = false;
      button.textContent = originalText;
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const chooseButton = event.target.closest(chooseSelector);
    if (chooseButton) {
      const quickAdd = chooseButton.closest('quick-add-component');
      if (quickAdd?.dataset.quickAddButton !== 'choose' && !chooseButton.classList.contains('quick-add__button--choose')) return;

      const productUrl = productUrlFromTrigger(chooseButton);
      if (!productUrl || !openDialog()) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      lastTrigger = chooseButton;
      showLoading();
      loadProduct(productUrl);
      return;
    }

    const closeButton = event.target.closest('[data-mqv-close]');
    if (closeButton) {
      event.preventDefault();
      closeDialog();
      return;
    }

    const optionButton = event.target.closest('[data-mqv-option-index]');
    if (optionButton && activeProduct) {
      const optionIndex = Number(optionButton.getAttribute('data-mqv-option-index'));
      const optionValue = optionButton.getAttribute('data-mqv-option-value');
      if (!Number.isInteger(optionIndex) || optionValue == null) return;

      const nextVariant = chooseFallbackVariant(optionIndex, optionValue);
      if (nextVariant) {
        selectedOptions = variantOptions(nextVariant);
        activeVariant = nextVariant;
        render();
      }
      return;
    }

    const thumb = event.target.closest('[data-mqv-image]');
    if (thumb) {
      updateMainImage(thumb.getAttribute('data-mqv-image'));
      return;
    }

    const addButton = event.target.closest('[data-mqv-add]');
    if (addButton) addToCart(addButton);
  }, true);

  document.addEventListener('DOMContentLoaded', () => {
    const dialog = getDialog();
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('close', () => {
      const trigger = lastTrigger;
      lastTrigger = null;
      if (trigger instanceof HTMLElement && document.contains(trigger)) trigger.focus({ preventScroll: true });
    });
  });
})();
